import { MutableRefObject } from 'react';
import { Network, DataSet } from 'vis-network/standalone';
import { Node, Edge } from '../types';
import { OrgGraphContextMenuProps } from '../OrgGraphContextMenu';
import { drawOverlayIcons } from '../lib/canvasUtils';
import { getGraphTypesCompositeKey } from '../lib/styleProcessor';
import { filterNodesForGraphType, getReachableNodesFromSource } from '../graphFilter';
import { HiddenStateStack } from '../hiddenStateStack';

export interface UseGraphEventsProps {
  networkRef: MutableRefObject<Network | null>;
  graphNodes: DataSet<Node>;
  graphEdges: DataSet<Edge>;
  expandedNodes: Set<string>;
  isExpanding: MutableRefObject<boolean>;
  dragSelectionActiveRef: MutableRefObject<boolean>;
  selectionRectRef: MutableRefObject<{startX: number, startY: number, endX: number, endY: number} | null>;
  isSelectingRef: MutableRefObject<boolean>;
  setStabilizationProgress: (progress: number) => void;
  setStabilizationComplete: (complete: boolean) => void;
  containerRef: MutableRefObject<HTMLDivElement | null>;
  setContextMenuParams: (params: OrgGraphContextMenuProps | null) => void;
  setShowContextMenu: (show: boolean) => void;
  graphMetaData: any;
  selectedGraphTypes: string[];
  userHiddenNodes: Set<string>;
  hiddenStateStack: MutableRefObject<HiddenStateStack>;
  setUserHiddenNodes: (nodes: Set<string>) => void;
  updateNodePosition: (nodeId: string, newPosition: { x: number; y: number }) => void;
  showCompanyComments?: boolean;
  showIvoLicenses?: boolean;
  onSelectedNodeChange?: (nodeId: string | null) => void;
  onInitialStabilizationDone?: () => void;
  onHideBranch?: (nodeId: string) => void;
}

export const useGraphEvents = ({
  networkRef,
  graphNodes,
  graphEdges,
  expandedNodes,
  isExpanding,
  dragSelectionActiveRef,
  selectionRectRef,
  isSelectingRef,
  setStabilizationProgress,
  setStabilizationComplete,
  containerRef,
  setContextMenuParams,
  setShowContextMenu,
  graphMetaData,
  selectedGraphTypes,
  userHiddenNodes,
  hiddenStateStack,
  setUserHiddenNodes,
  updateNodePosition,
  showCompanyComments = true,
  showIvoLicenses = false,
  onSelectedNodeChange,
  onInitialStabilizationDone,
  onHideBranch
}: UseGraphEventsProps) => {
  
  const events = {
    afterDrawing: (ctx: CanvasRenderingContext2D) => {
      if (networkRef.current) {
        drawOverlayIcons(
          ctx,
          networkRef.current,
          graphNodes,
          expandedNodes,
          graphMetaData.sourceNodeId,
          showCompanyComments,
          showIvoLicenses,
        );
      }
      
      // Draw selection rectangle if selecting
      if (dragSelectionActiveRef.current && selectionRectRef.current) {
        const { startX, startY, endX, endY } = selectionRectRef.current;
        ctx.save();
        ctx.strokeStyle = 'rgba(0, 100, 255, 0.8)';
        ctx.lineWidth = 1;
        ctx.fillStyle = 'rgba(0, 100, 255, 0.2)';
        ctx.beginPath();
        ctx.rect(startX, startY, endX - startX, endY - startY);
        ctx.fill();
        ctx.stroke();
        ctx.restore();
      }
    },
    dragStart: (params: any) => {
      if (dragSelectionActiveRef.current && params.nodes.length === 0 && params.edges.length === 0) {
        isSelectingRef.current = true;
        const { x, y } = params.pointer.DOM;
        selectionRectRef.current = { startX: x, startY: y, endX: x, endY: y };
      }
    },
    dragging: (params: any) => {
      if (isSelectingRef.current && selectionRectRef.current) {
        const { x, y } = params.pointer.DOM;
        selectionRectRef.current.endX = x;
        selectionRectRef.current.endY = y;
        networkRef.current?.redraw(); // Trigger afterDrawing to draw the rect
      }
    },
    dragEnd: (params: any) => {
      if (isSelectingRef.current && selectionRectRef.current) {
        const { startX, startY, endX, endY } = selectionRectRef.current;
        
        // Calculate selection bounds in DOM coordinates
        const minX = Math.min(startX, endX);
        const maxX = Math.max(startX, endX);
        const minY = Math.min(startY, endY);
        const maxY = Math.max(startY, endY);
        
        // Select nodes within the rectangle
        if (networkRef.current) {
          const nodeIds = graphNodes.getIds();
          const selectedNodes: string[] = [];
          
          nodeIds.forEach((nodeId) => {
            const nodePos = networkRef.current!.getPosition(nodeId);
            const domPos = networkRef.current!.canvasToDOM(nodePos);
            
            if (
              domPos.x >= minX &&
              domPos.x <= maxX &&
              domPos.y >= minY &&
              domPos.y <= maxY
            ) {
              selectedNodes.push(nodeId as string);
            }
          });
          
          networkRef.current.selectNodes(selectedNodes);
        }
        
        // Reset selection
        isSelectingRef.current = false;
        selectionRectRef.current = null;
        networkRef.current?.redraw();
      }

      // Handle manual node repositioning after drag
      if (params.nodes.length > 0) {
        const nodeId = params.nodes[0];
        
        // Get the new position from the network
        if (networkRef.current) {
          const newPositions = networkRef.current.getPositions([nodeId]);
          const newPosition = newPositions[nodeId];
          
          if (newPosition) {
            updateNodePosition(nodeId, newPosition);
          }
        }
      }
    },
    stabilizationProgress: (event: any) => {
      const percentComplete = Math.round(event.iterations / event.total * 100);
      setStabilizationProgress(percentComplete);
    },
    stabilizationIterationsDone: () => {
      networkRef.current?.storePositions();
      networkRef.current?.setOptions({ physics: { enabled: false } }); // Disable physics after stabilization
      
      // Skip viewport shift during node expansion — the expansion hook
      // saves and restores the viewport itself.
      if (!isExpanding.current) {
        // Shift viewport immediately to prevent visual jumping (initial load only)
        if (
          networkRef.current &&
          containerRef.current &&
          typeof window !== 'undefined' &&
          window.location &&
          window.location.pathname === '/companyassessment'
        ) {
          const width = containerRef.current.clientWidth;
          networkRef.current.moveTo({
            offset: { x: width * 0.20, y: 0 }
          });
        }
      }

      setStabilizationComplete(true);
      
      // After initial stabilization, trigger slide animation if available
      if (!isExpanding.current && onInitialStabilizationDone) {
        onInitialStabilizationDone();
      }
      
      // After initial stabilization, nodes should be freely draggable
      // No need to fix them - fixing only happens during expansion
    },
    oncontext: (event: any) => {
      const { pointer } = event;

      const nodeId = networkRef.current?.getNodeAt(pointer.DOM);
      if (!nodeId) {
        return;
      }

      const node = graphNodes.get().find((n) => n.id === nodeId);
      if (!node || !["Company", "Address", "FunktionarPerson"].includes(node.type)) {
        return;
      }

      setContextMenuParams({ x: event.event.clientX, y: event.event.clientY, node: node });
      setShowContextMenu(true);

      event.event.preventDefault();
    },
    click: (event: any) => {
      if (event.nodes.length > 0) {
        onSelectedNodeChange?.(String(event.nodes[0]));
        return;
      }

      onSelectedNodeChange?.(null);
    },
    selectNode: (event: any) => {
      const nodeId = event.nodes?.[0];
      onSelectedNodeChange?.(nodeId ? String(nodeId) : null);
    },
    deselectNode: () => {
      onSelectedNodeChange?.(null);
    },
    doubleClick: (event: any) => {
      if (event.nodes.length === 0 || event.nodes[0] == graphMetaData.sourceNodeId) {
        return;
      }
      const nodeId = event.nodes[0];

      if (onHideBranch) {
        onHideBranch(String(nodeId));
        event.event?.preventDefault();
        return;
      }
      
      // Create a composite key for the current selection of graph types
      const graphTypesKey = getGraphTypesCompositeKey(selectedGraphTypes);
      
      // Get nodes for the current selection
      let allFilteredNodes = new Set<string>();
      
      if (selectedGraphTypes.includes('all')) {
        // If 'all' is selected, include all nodes
        allFilteredNodes = new Set(graphNodes.get().map(n => n.id));
      } else {
        // Otherwise, filter nodes based on the selected graph types
        const typeNodes = filterNodesForGraphType(selectedGraphTypes, graphEdges, networkRef.current!);
        typeNodes.forEach(nodeId => allFilteredNodes.add(nodeId));
      };
      
      const unhiddenNodes = new Set<string>(Array.from(allFilteredNodes).filter((nodeId) => !userHiddenNodes.has(nodeId)));
      const reachableNodes = getReachableNodesFromSource(unhiddenNodes, networkRef.current!, graphMetaData.sourceNodeId, nodeId.toString());
      const newHiddenNodes = new Set<string>(Array.from(unhiddenNodes).filter((nodeId) => !reachableNodes.has(nodeId)));

      hiddenStateStack.current.insertState(graphTypesKey, newHiddenNodes);
      setUserHiddenNodes(hiddenStateStack.current.getState(graphTypesKey));

      event.event.preventDefault();
    },

  };

  return events;
};
